import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { queryCodeDirectories } from '@/features/codeCoverage/data/codeDirectories';
import { queryCoverageFiles } from '@/features/codeCoverage/data/coverageFiles';
import { queryCoverageRuns } from '@/features/codeCoverage/data/coverageRuns';
import { queryExecutionRuns } from '@/features/executionRuns/data/executionRuns';
import { querySuiteRuns, queryTestRuns } from '@/features/executions/data';
import { queryFeatures } from '@/features/features/data/features';
import { queryJobRuns, queryJobs } from '@/features/jobs/data/jobs';
import {
  queryPipelineRunSummary,
  queryPipelines,
  queryPipelineStageExecutions,
  queryPipelineStages,
} from '@/features/pipelines/data';
import { queryReleases } from '@/features/releases/data/releases';
import { querySuites } from '@/features/suites/data/suites';
import { queryTests } from '@/features/tests/data/tests';
import { queryResultsFiles, queryUploads } from '@/features/uploads/data';
import { getTargetActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { queryKeys } from '@/shared/hooks';
import { Box, InfoField, Panel, Stack, Text } from '@/ui';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';
import {
  settingsCopyTextSx,
  settingsPanelSurfaceSx,
} from '@/ui/composites/settingsPageTypographySx';
import { CopyIcon, IconButton } from '@/ui/primitives';
import {
  projectSettingsOverviewDividerSx,
  projectSettingsOverviewLabelTextSx,
  projectSettingsOverviewPanelSx,
  projectSettingsOverviewPairLabelSx,
  projectSettingsOverviewRowSx,
  projectSettingsProjectIdCopyContainerSx,
  projectSettingsProjectIdCopyButtonSx,
  projectSettingsProjectIdTextSx,
} from './projectSettingsSurfaceSx';
import type { ProjectDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

type ProjectOverviewData = {
  releasesTotal: number;
  featuresTotal: number;
  uploadsTotal: number;
  resultsFilesTotal: number;
  executionRunsTotal: number;
  suitesTotal: number;
  suiteRunsTotal: number;
  testsTotal: number;
  testRunsTotal: number;
  coverageRunsTotal: number;
  codeDirectoriesTotal: number;
  coverageFilesTotal: number;
  pipelinesTotal: number;
  pipelineRunsTotal: number;
  stagesTotal: number;
  stageRunsTotal: number;
  jobsTotal: number;
  jobRunsTotal: number;
};

type ProjectModuleKey = 'delivery' | 'code' | 'test' | 'operations';

const ProjectOverviewRow = ({
  label,
  value,
}: {
  label: ReactElement | string;
  value: string;
}): ReactElement => (
  <Box sx={projectSettingsOverviewRowSx}>
    {typeof label === 'string' ? (
      <Text role="body" color="textSecondary" sx={projectSettingsOverviewLabelTextSx}>
        {label}
      </Text>
    ) : (
      label
    )}
    <Text role="body">{value}</Text>
  </Box>
);

const ProjectOverviewPairLabel = ({ primary }: { primary: string }): ReactElement => (
  <Box sx={projectSettingsOverviewPairLabelSx}>
    <Text role="body" color="textSecondary" sx={projectSettingsOverviewLabelTextSx}>
      {primary}
    </Text>
    <Text role="body" color="textSecondary" sx={projectSettingsOverviewDividerSx}>
      /
    </Text>
    <Text role="body" color="textSecondary" sx={projectSettingsOverviewLabelTextSx}>
      Runs
    </Text>
  </Box>
);

const ProjectModulePanel = ({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ key: string; label: ReactElement | string; value: string }>;
}): ReactElement => (
  <Panel surfaceVariant="settings" sx={[settingsPanelSurfaceSx, projectSettingsOverviewPanelSx]}>
    <Stack gap="sm">
      <Text role="body">{title}</Text>
      {rows.map((row) => (
        <ProjectOverviewRow key={row.key} label={row.label} value={row.value} />
      ))}
    </Stack>
  </Panel>
);

const useProjectOverviewData = ({
  projectId,
  organisationId,
  modules,
}: {
  projectId: string | null;
  organisationId: string | null;
  modules: ProjectModuleKey[];
}): {
  projectId: string | null;
  overviewData: ProjectOverviewData | undefined;
  resolveCount: (value: number | null | undefined) => string;
} => {
  const projectIds = useMemo(() => (projectId ? [projectId] : []), [projectId]);
  const projectIdsKey = useMemo(() => JSON.stringify(projectIds), [projectIds]);
  const projectFilter = useMemo(() => buildEqFilter('projectId', projectId), [projectId]);
  const modulesKey = useMemo(() => [...modules].sort().join(','), [modules]);
  const overviewQuery = useQuery({
    queryKey: queryKeys.projectSettingsOverview(
      organisationId ?? null,
      `${projectIdsKey}:${modulesKey}`,
    ),
    enabled: projectIds.length > 0 && Boolean(projectId),
    queryFn: async () => {
      const emptyCountResult = { total: 0 };
      const emptyPipelineRunSummary = { buildTotal: 0 };
      const overviewData: ProjectOverviewData = {
        releasesTotal: 0,
        featuresTotal: 0,
        uploadsTotal: 0,
        resultsFilesTotal: 0,
        executionRunsTotal: 0,
        suitesTotal: 0,
        suiteRunsTotal: 0,
        testsTotal: 0,
        testRunsTotal: 0,
        coverageRunsTotal: 0,
        codeDirectoriesTotal: 0,
        coverageFilesTotal: 0,
        pipelinesTotal: 0,
        pipelineRunsTotal: 0,
        stagesTotal: 0,
        stageRunsTotal: 0,
        jobsTotal: 0,
        jobRunsTotal: 0,
      };
      const shouldQueryDelivery = modules.includes('delivery');
      const shouldQueryCode = modules.includes('code');
      const shouldQueryTest = modules.includes('test');
      const shouldQueryOperations = modules.includes('operations');

      await Promise.all([
        ...(shouldQueryDelivery
          ? [
              queryReleases(projectId, organisationId, {
                page: 1,
                pageSize: 1,
                sorts: [],
                filters: [],
              })
                .then((result) => {
                  overviewData.releasesTotal = result.total;
                })
                .catch(() => {
                  overviewData.releasesTotal = emptyCountResult.total;
                }),
              queryFeatures(projectId, organisationId, {
                page: 1,
                pageSize: 1,
                sorts: [],
                filters: [],
              })
                .then((result) => {
                  overviewData.featuresTotal = result.total;
                })
                .catch(() => {
                  overviewData.featuresTotal = emptyCountResult.total;
                }),
            ]
          : []),
        ...(shouldQueryTest
          ? [
              queryUploads({
                page: 1,
                pageSize: 1,
                filters: projectFilter,
                summaryMode: 'none',
              })
                .then((result) => {
                  overviewData.uploadsTotal = result.total;
                })
                .catch(() => {
                  overviewData.uploadsTotal = emptyCountResult.total;
                }),
              queryResultsFiles({
                page: 1,
                pageSize: 1,
                filters: projectFilter,
                includeSummary: false,
              })
                .then((result) => {
                  overviewData.resultsFilesTotal = result.total;
                })
                .catch(() => {
                  overviewData.resultsFilesTotal = emptyCountResult.total;
                }),
              queryExecutionRuns(
                {
                  page: 1,
                  pageSize: 1,
                  filters: projectFilter,
                  sorts: [],
                },
                organisationId,
              )
                .then((result) => {
                  overviewData.executionRunsTotal = result.total;
                })
                .catch(() => {
                  overviewData.executionRunsTotal = emptyCountResult.total;
                }),
              querySuites(projectId, organisationId, {
                page: 1,
                pageSize: 1,
                sorts: [],
                filters: [],
              })
                .then((result) => {
                  overviewData.suitesTotal = result.total;
                })
                .catch(() => {
                  overviewData.suitesTotal = emptyCountResult.total;
                }),
              querySuiteRuns(projectId, organisationId, {
                page: 1,
                pageSize: 1,
                sorts: [],
                filters: [],
              })
                .then((result) => {
                  overviewData.suiteRunsTotal = result.total;
                })
                .catch(() => {
                  overviewData.suiteRunsTotal = emptyCountResult.total;
                }),
              queryTests(projectId, organisationId, {
                page: 1,
                pageSize: 1,
                sorts: [],
                filters: [],
              })
                .then((result) => {
                  overviewData.testsTotal = result.total;
                })
                .catch(() => {
                  overviewData.testsTotal = emptyCountResult.total;
                }),
              queryTestRuns(projectId, organisationId, {
                page: 1,
                pageSize: 1,
                sorts: [],
                filters: [],
              })
                .then((result) => {
                  overviewData.testRunsTotal = result.total;
                })
                .catch(() => {
                  overviewData.testRunsTotal = emptyCountResult.total;
                }),
            ]
          : []),
        ...(shouldQueryCode
          ? [
              queryCoverageRuns({ page: 1, pageSize: 1, filters: projectFilter }, organisationId)
                .then((result) => {
                  overviewData.coverageRunsTotal = result.total;
                })
                .catch(() => {
                  overviewData.coverageRunsTotal = emptyCountResult.total;
                }),
              queryCodeDirectories({ page: 1, pageSize: 1, filters: projectFilter }, organisationId)
                .then((result) => {
                  overviewData.codeDirectoriesTotal = result.total;
                })
                .catch(() => {
                  overviewData.codeDirectoriesTotal = emptyCountResult.total;
                }),
              queryCoverageFiles({ page: 1, pageSize: 1, filters: projectFilter }, organisationId)
                .then((result) => {
                  overviewData.coverageFilesTotal = result.total;
                })
                .catch(() => {
                  overviewData.coverageFilesTotal = emptyCountResult.total;
                }),
            ]
          : []),
        ...(shouldQueryOperations
          ? [
              queryPipelines(projectId, organisationId, {
                page: 1,
                pageSize: 1,
                sorts: [{ field: 'createdAt', direction: 'desc' }],
                filters: [],
              })
                .then((result) => {
                  overviewData.pipelinesTotal = result.total;
                })
                .catch(() => {
                  overviewData.pipelinesTotal = emptyCountResult.total;
                }),
              queryPipelineRunSummary(projectId, organisationId, {
                page: 1,
                pageSize: 1,
                sorts: [],
                filters: [],
              })
                .then((result) => {
                  overviewData.pipelineRunsTotal = result.buildTotal;
                })
                .catch(() => {
                  overviewData.pipelineRunsTotal = emptyPipelineRunSummary.buildTotal;
                }),
              queryPipelineStages(projectId, organisationId, {
                page: 1,
                pageSize: 1,
                sorts: [{ field: 'startedAt', direction: 'desc' }],
                filters: [],
              })
                .then((result) => {
                  overviewData.stagesTotal = result.total;
                })
                .catch(() => {
                  overviewData.stagesTotal = emptyCountResult.total;
                }),
              queryPipelineStageExecutions(projectId, organisationId, {
                page: 1,
                pageSize: 1,
                sorts: [{ field: 'startedAt', direction: 'desc' }],
                filters: [],
              })
                .then((result) => {
                  overviewData.stageRunsTotal = result.total;
                })
                .catch(() => {
                  overviewData.stageRunsTotal = emptyCountResult.total;
                }),
              queryJobs(projectId, organisationId, {
                page: 1,
                pageSize: 1,
                sorts: [{ field: 'jobName', direction: 'asc' }],
                filters: [],
              })
                .then((result) => {
                  overviewData.jobsTotal = result.total;
                })
                .catch(() => {
                  overviewData.jobsTotal = emptyCountResult.total;
                }),
              queryJobRuns(projectId, organisationId, {
                page: 1,
                pageSize: 1,
                sorts: [{ field: 'startedAt', direction: 'desc' }],
                filters: [],
              })
                .then((result) => {
                  overviewData.jobRunsTotal = result.total;
                })
                .catch(() => {
                  overviewData.jobRunsTotal = emptyCountResult.total;
                }),
            ]
          : []),
      ]);

      return overviewData;
    },
  });

  const resolveCount = (value: number | null | undefined): string => {
    if (overviewQuery.isLoading && value === undefined) {
      return '-';
    }

    return (value ?? 0).toLocaleString('en-GB');
  };

  return {
    projectId,
    overviewData: overviewQuery.data,
    resolveCount,
  };
};

export const ProjectSettingsDetailsRail = ({
  project,
  onCopyProjectId,
}: {
  project: ProjectDto | null;
  onCopyProjectId: () => void;
}): ReactElement | null => {
  const createdDate = new Date(project?.createdAt ?? '');
  const createdLabel = Number.isNaN(createdDate.valueOf())
    ? '-'
    : createdDate.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });

  if (!project) {
    return null;
  }

  return (
    <Panel surfaceVariant="settings" sx={[settingsPanelSurfaceSx, projectSettingsOverviewPanelSx]}>
      <Stack gap="sm">
        <InfoField
          label="Project ID"
          gap="xxs"
          labelTextRole="body"
          value={
            <Box sx={projectSettingsProjectIdCopyContainerSx}>
              <Text role="body" component="span" sx={projectSettingsProjectIdTextSx}>
                {project.id}
              </Text>
              <IconButton
                size="small"
                aria-label={getTargetActionLabel('copy', 'project ID')}
                onClick={onCopyProjectId}
                sx={projectSettingsProjectIdCopyButtonSx}
              >
                <CopyIcon />
              </IconButton>
            </Box>
          }
          labelSx={settingsCopyTextSx}
        />
        <InfoField
          label={getDetailLabel('created')}
          gap="xxs"
          labelTextRole="body"
          value={createdLabel}
          labelSx={settingsCopyTextSx}
        />
      </Stack>
    </Panel>
  );
};

export const ProjectModulePanelsComparison = ({
  projectId,
  organisationId,
}: {
  projectId: string | null;
  organisationId: string | null;
}): ReactElement | null => {
  return (
    <ProjectModuleOverviewPanels
      projectId={projectId}
      organisationId={organisationId}
      modules={['delivery', 'code', 'test', 'operations']}
    />
  );
};

export const ProjectModuleOverviewPanels = ({
  projectId,
  organisationId,
  modules,
}: {
  projectId: string | null;
  organisationId: string | null;
  modules: ProjectModuleKey[];
}): ReactElement | null => {
  const {
    projectId: resolvedProjectId,
    overviewData,
    resolveCount,
  } = useProjectOverviewData({
    projectId,
    organisationId,
    modules,
  });

  if (!resolvedProjectId) {
    return null;
  }

  const modulePanels: Record<ProjectModuleKey, ReactElement> = {
    delivery: (
      <ProjectModulePanel
        title="Delivery Module Data"
        rows={[
          { key: 'releases', label: 'Releases', value: resolveCount(overviewData?.releasesTotal) },
          { key: 'features', label: 'Features', value: resolveCount(overviewData?.featuresTotal) },
        ]}
      />
    ),
    code: (
      <ProjectModulePanel
        title="Code Module Data"
        rows={[
          {
            key: 'coverage-runs',
            label: 'Coverage Runs',
            value: resolveCount(overviewData?.coverageRunsTotal),
          },
          {
            key: 'directories-runs',
            label: <ProjectOverviewPairLabel primary="Directories" />,
            value: `${resolveCount(overviewData?.codeDirectoriesTotal)} / ${resolveCount(overviewData?.coverageRunsTotal)}`,
          },
          {
            key: 'files-runs',
            label: <ProjectOverviewPairLabel primary="Files" />,
            value: `${resolveCount(overviewData?.coverageFilesTotal)} / ${resolveCount(overviewData?.coverageRunsTotal)}`,
          },
        ]}
      />
    ),
    test: (
      <ProjectModulePanel
        title="Test Module Data"
        rows={[
          { key: 'uploads', label: 'Uploads', value: resolveCount(overviewData?.uploadsTotal) },
          {
            key: 'results-files',
            label: 'Results Files',
            value: resolveCount(overviewData?.resultsFilesTotal),
          },
          {
            key: 'execution-runs',
            label: 'Execution Runs',
            value: resolveCount(overviewData?.executionRunsTotal),
          },
          {
            key: 'suites-runs',
            label: <ProjectOverviewPairLabel primary="Suites" />,
            value: `${resolveCount(overviewData?.suitesTotal)} / ${resolveCount(overviewData?.suiteRunsTotal)}`,
          },
          {
            key: 'tests-runs',
            label: <ProjectOverviewPairLabel primary="Tests" />,
            value: `${resolveCount(overviewData?.testsTotal)} / ${resolveCount(overviewData?.testRunsTotal)}`,
          },
        ]}
      />
    ),
    operations: (
      <ProjectModulePanel
        title="Operations"
        rows={[
          {
            key: 'pipelines-runs',
            label: <ProjectOverviewPairLabel primary="Pipelines" />,
            value: `${resolveCount(overviewData?.pipelinesTotal)} / ${resolveCount(overviewData?.pipelineRunsTotal)}`,
          },
          {
            key: 'stages-runs',
            label: <ProjectOverviewPairLabel primary="Stages" />,
            value: `${resolveCount(overviewData?.stagesTotal)} / ${resolveCount(overviewData?.stageRunsTotal)}`,
          },
          {
            key: 'jobs-runs',
            label: <ProjectOverviewPairLabel primary="Jobs" />,
            value: `${resolveCount(overviewData?.jobsTotal)} / ${resolveCount(overviewData?.jobRunsTotal)}`,
          },
        ]}
      />
    ),
  };

  return (
    <Stack gap="lg">
      {modules.map((module) => (
        <Box key={module}>{modulePanels[module]}</Box>
      ))}
    </Stack>
  );
};
